/**
 * DriverAnalysisResult — Driver Analysis result renderer.
 *
 * Spec: v2-design/RESULT_RENDERING_v0_spec.md §4.
 *
 * Uses DriverAnalysisGenerateChartPanel (`./driver-analysis/GenerateChartPanel`,
 * a DA-specific chart panel) — NOT the same component as ScenarioManager's
 * general-purpose GenerateChartPanel; spec §4.1 is explicit about not
 * confusing them.
 *
 * Invocation shape:
 *   <GenerateChartPanel data={message.chartData.data} open={true} formatKey={0} />
 *
 * The backend `chart_payload` wraps the DA data array under `.data`
 * (mirrors the Chart Visualization payload shape — see ChartVizResult).
 * We unwrap before passing through.
 *
 * Size constraint per spec §4.5: DriverAnalysisGenerateChartPanel sets the
 * AG Grid container to a fixed height, so AG Grid's internal viewport
 * scrolls when the DA table exceeds it. We do NOT wrap in an overflow:auto
 * Box here — that would yield a second scrollbar atop AG Grid's own. The
 * "first N rows + Show all" expander from §4.4 is a v0.X enhancement.
 */
import React from 'react';
import { Box } from '@mui/material';

import DriverAnalysisGenerateChartPanel, {
  DriverAnalysisChartData,
} from './driver-analysis/GenerateChartPanel';

interface DriverAnalysisChartPayload {
  data?: DriverAnalysisChartData[];
  Status?: unknown;
}

export interface DriverAnalysisResultProps {
  // The backend-provided chart payload (verbatim driver analysis API
  // response). The DA result array lives under `.data`.
  data: DriverAnalysisChartPayload | unknown;
  formatKey?: number;
}

export const DriverAnalysisResult: React.FC<DriverAnalysisResultProps> = ({
  data,
  formatKey = 0,
}) => {
  const rows = (data as DriverAnalysisChartPayload | undefined)?.data;
  const hasRows = Array.isArray(rows) && rows.length > 0;

  if (!hasRows) {
    return (
      <Box
        sx={{
          height: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          fontSize: 13,
          textAlign: 'center',
          px: 2,
        }}
      >
        <Box sx={{ fontWeight: 600, mb: 0.5 }}>
          Driver Analysis data unavailable
        </Box>
        <Box sx={{ fontSize: 12 }}>
          The backend returned a payload without a `data` array. Check
          Agentic-API logs and the driver_analysis API response.
        </Box>
      </Box>
    );
  }

  // Size constraint per spec §4.5: bound the panel so long DA tables
  // don't blow out the card. AG Grid's internal viewport provides the
  // scrollbar; we DON'T wrap in an overflow:auto Box (would yield a
  // second scrollbar atop AG Grid's own).
  return (
    <DriverAnalysisErrorBoundary>
      <DriverAnalysisGenerateChartPanel
        data={rows}
        open={true}
        formatKey={formatKey}
      />
    </DriverAnalysisErrorBoundary>
  );
};

class DriverAnalysisErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[DriverAnalysisResult] render failed', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <Box
          sx={{
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            fontSize: 13,
            textAlign: 'center',
            px: 2,
          }}
        >
          <Box sx={{ fontWeight: 600, mb: 0.5 }}>
            Driver Analysis render failed
          </Box>
          <Box sx={{ fontSize: 12 }}>
            {this.state.error.message}. See console for full error.
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}
